import { useTranslation } from "react-i18next";
import { Link } from "react-router-dom";

import { CircleArrowRightIcon, RocketIcon } from "@lucide/icons";

import { groupBannerImage, groupCoverImage } from "simplydo/core/images";
import { styled } from "simplydo/react/styled";
import { useTheme } from "simplydo/react/theme";
import { CardContent, CardMeta } from "simplydo/react/ui/card";
import { H3, H4 } from "simplydo/react/ui/header";
import { Icon } from "simplydo/react/ui/icon";
import { Label } from "simplydo/react/ui/label";

import util from "utils/utils";

import { ShadowedCard } from "components/lib/UI";

import type { GroupCardGroup } from "./types";

const CoverImage = styled.div<{ $coverImage: string }>`
  height: 40px;
  min-width: 40px;
  margin-right: 5px;
  border-radius: 5px;
  background-color: ${({ theme }) => theme.secondaryColour};
  background-image: ${({ $coverImage }) => `url(${$coverImage})`};
  background-size: cover;
  background-repeat: no-repeat;
  background-position: center center;
  cursor: pointer;
  &:hover {
    box-shadow: inset 0 0 0 2000px rgba(211, 211, 211, 0.3);
  }
`;

const GroupInfo = styled.div`
  display: flex;
  flex: 1;
  flex-direction: column;
  align-items: flex-start;
  max-height: 40px;
  padding-right: 5px;
  margin: 0 10px 0 0;
  width: 40%;
  h4 {
    text-overflow: ellipsis;
    overflow: hidden;
    white-space: nowrap;
    max-width: 100%;
    margin-bottom: 2px;
    cursor: pointer;
    &:hover {
      text-decoration: underline;
    }
  }
  p {
    text-overflow: ellipsis;
    white-space: nowrap;
    overflow: hidden;
  }
  > div {
    display: flex;
    flex-direction: row;
    align-items: center;
    max-width: 100%;
  }
`;

const GroupMeta = styled.div`
  display: flex;
  height: 100%;
  flex-direction: column;
  align-items: flex-start;
  justify-content: center;
  margin: 0;
`;

const GroupCardContainer = styled(Link)`
  display: flex;
  align-self: stretch;
  align-items: center;
  padding: 7.5px;
  border-radius: 5px;
  height: 55px;
  flex: 1;
  h4 {
    margin: 0;
  }
  background-color: #fff;
  max-width: 100%;
  border: 1px solid #e9ebee;
  cursor: pointer;
  &:not(:last-of-type) {
    margin-bottom: 10px;
  }
  &:hover {
    background-color: #eeeff1;
  }
  a,
  p,
  h4 {
    color: #000;
  }
`;

const GroupCard = ({ group }: { group: GroupCardGroup }) => {
  const { t } = useTranslation();
  const theme = useTheme();
  const groupLink = `/groups/${group._id}`;
  const hasCoverAndBanner = Boolean(group?.coverImage && group.bannerImage);

  return (
    <ShadowedCard>
      <Link to={groupLink}>
        <div
          style={{
            width: "100%",
            height: "75px",
            position: "relative",
            backgroundImage: `url(${groupBannerImage(group)})`,
            backgroundColor: theme.secondaryColour,
            backgroundSize: "cover",
            backgroundRepeat: "no-repeat",
            backgroundPosition: "center center",
            borderTopLeftRadius: 4,
            borderTopRightRadius: 4,
          }}
        />
      </Link>

      <CardContent style={{ position: "relative" }}>
        {hasCoverAndBanner ? (
          <>
            <div
              style={{
                width: 80,
                height: 80,
                overflow: "hidden",
                position: "absolute",
                marginTop: -80,
                boxShadow: "rgb(0 0 0 / 30%) 0.5px 2px 10px 4px",
                border: "2px solid white",
                backgroundImage: `url(${groupCoverImage(group)})`,
                backgroundSize: "cover",
                backgroundRepeat: "no-repeat",
                backgroundPosition: "center center",
                borderRadius: 7,
              }}
            />
            <H3
              style={{
                marginBottom: group.description ? 5 : undefined,
                marginTop: 5,
              }}
            >
              <Link to={groupLink} style={{ color: "#4d4949" }}>
                {group.name}
              </Link>
            </H3>
          </>
        ) : (
          <H3
            style={{
              display: "block",
              flex: 1,
              marginBottom: group.description ? 5 : undefined,
            }}
          >
            <Link to={groupLink} style={{ color: "#4d4949" }}>
              {group.name}
            </Link>
          </H3>
        )}

        {group.description ? <CardMeta>{group.description}</CardMeta> : null}
      </CardContent>

      <CardContent
        extra
        style={{
          backgroundColor: theme.primaryColour,
          textAlign: "center",
          fontWeight: 600,
          maxHeight: 50,
        }}
      >
        <Link
          to={groupLink}
          style={{ color: "#fff", textTransform: "uppercase" }}
        >
          {t("generic.viewType", { entity: t("generic.group") })}{" "}
          <Icon icon={CircleArrowRightIcon} />
        </Link>
      </CardContent>
    </ShadowedCard>
  );
};

export const GroupCardListView = ({ group }: { group: GroupCardGroup }) => (
  <GroupCardContainer to={`/groups/${group._id}`}>
    <CoverImage
      $coverImage={
        group?.coverImage
          ? util.groupCoverImage(group, "300x300")
          : group?.bannerImage
            ? util.groupBannerImage(group)
            : "https://cdn.simplydo.co.uk/images/groupCoverImage.png"
      }
    />
    <GroupInfo>
      <div>
        <H4>{group.name}</H4>
      </div>
      {group.description ? (
        <div>
          <p>{group.description}</p>
        </div>
      ) : null}
    </GroupInfo>
    <GroupMeta>
      {group.isLeader ? (
        <Label size="small" color="purple">
          <Icon icon={RocketIcon} />
          Leader
        </Label>
      ) : null}
    </GroupMeta>
  </GroupCardContainer>
);

export default GroupCard;
